// src/components/assets/FilePreviewList.tsx

import React, { useEffect, useState } from 'react';
import {
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  IconButton,
  Box,
  FormControl,
  MenuItem,
  TextField,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import { ImageTypeKey } from '../../../context/AddAssetContext';

// Constants for the exterior tags
const EXTERIOR_TAG_OPTIONS = [
  "Unassigned", "Left Front", "Left Side", "Left Rear", "Rear",
  "Right Rear", "Right Side", "Right Front", "Front", "Roof"
];

interface FilePreviewListProps {
  files: File[];
  onRemove: (fileName: string) => void;
  currentImageType: ImageTypeKey;
  imageTags: Record<string, string>;
  onSetTag: (fileName: string, tag: string) => void;
}

const FilePreviewList: React.FC<FilePreviewListProps> = ({ 
  files, 
  onRemove,
  currentImageType,
  imageTags,
  onSetTag,
 }) => {
  const [previews, setPreviews] = useState<Record<string, string>>({});

  useEffect(() => {
    const newPreviews: Record<string, string> = {};
    files.forEach(file => {
      newPreviews[file.name] = URL.createObjectURL(file);
    });

    setPreviews(newPreviews);

    // Cleanup function to revoke the object URLs and prevent memory leaks
    return () => {
      Object.values(newPreviews).forEach(url => URL.revokeObjectURL(url));
    };
  }, [files]);

  if (files.length === 0) {
    return (
      <Typography sx={{ mt: 4, textAlign: 'center', color: 'text.secondary' }}>
        No files selected for this category.
      </Typography>
    );
  }

  return (
    // The Grid container now has tighter spacing
    <Grid container spacing={1} sx={{ mt: 1 }}>
      {files.map(file => (
        <Grid item xs={6} key={file.name}> {/* Sticking with 2-across */}
          <Card sx={{ position: 'relative' }}>
            <CardMedia
              component="img"
              height="100" // Reduced height for a more compact card
              image={previews[file.name]}
              alt={file.name}
            />
            {/* Using a simple Box instead of CardContent for tighter control */}
            <Box sx={{ p: 1 }}>
              <Typography noWrap variant="caption" display="block" title={file.name}>
                {file.name}
              </Typography>
              
              {currentImageType === 'exterior' ? (
                <Select
                  data-cy={`file-preview-tag-${file.name}`}
                  fullWidth
                  variant="standard"
                  size="small"
                  value={imageTags[file.name] || 'Unassigned'}
                  onChange={(e: SelectChangeEvent) => onSetTag(file.name, e.target.value)}
                  // Styling to make the select look clean and handle overflow
                  sx={{
                    fontSize: '0.8rem',
                    '& .MuiSelect-select': {
                      // These two properties are key for text truncation
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    },
                    // Remove default underline
                    '&:before': { border: 'none' },
                    '&:after': { border: 'none' },
                    '&:hover:not(.Mui-disabled):before': { border: 'none' },
                  }}
                >
                  {EXTERIOR_TAG_OPTIONS.map(tag => (
                    <MenuItem key={tag} value={tag}>{tag}</MenuItem>
                  ))}
                </Select>
              ) : (
                // A simple input for other types
                 <input
                    data-cy={`file-preview-tag-input-${file.name}`}
                    type="text"
                    placeholder="Add a tag..."
                    value={imageTags[file.name] || ''}
                    onChange={(e) => onSetTag(file.name, e.target.value)}
                    style={{
                      width: '100%',
                      background: 'transparent',
                      border: 'none',
                      color: 'inherit',
                      fontSize: '0.8rem',
                      outline: 'none',
                      marginTop: '4px'
                    }}
                 />
              )}
            </Box>
            <IconButton
              data-cy={`file-preview-delete-${file.name}`}
              size="small"
              onClick={() => onRemove(file.name)}
              sx={{
                position: 'absolute',
                top: 4,
                right: 4,
                backgroundColor: 'rgba(0, 0, 0, 0.6)', // Darker background for better contrast
                color: 'white',
                '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.8)' } // Use a theme-like red on hover
              }}
            >
              <Delete fontSize="inherit" />
            </IconButton>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

export default FilePreviewList;